package com.tovtalk.data.network

import com.tovtalk.domain.entity.DomainResult
import kotlinx.coroutines.delay

/**
 * Retry transient failures (5xx, network, timeout) with exponential backoff.
 * Non-transient errors (4xx, parse errors without a cause) are returned immediately.
 */
suspend fun <T> retryOnTransient(
    maxAttempts: Int = 3,
    initialDelayMs: Long = 1_000L,
    block: suspend () -> DomainResult<T>
): DomainResult<T> {
    var last: DomainResult<T> = DomainResult.Error("Unknown")
    repeat(maxAttempts) { attempt ->
        last = block()
        when (val result = last) {
            is DomainResult.Success -> return result
            is DomainResult.Error -> if (!isTransientError(result)) return result
            is DomainResult.Loading -> { }
        }
        if (attempt < maxAttempts - 1) delay(initialDelayMs * (1 shl attempt))
    }
    return last
}

/**
 * HTTP status codes that are safe to retry: request timeout, too-many-requests
 * (rate limit) and the 5xx server-error family.
 *
 * NOTE: by the time we reach here the call has already been collapsed into a
 * [DomainResult.Error] — we no longer have the [HttpResponse], so we cannot read
 * a structured status or the `Retry-After` header. We therefore classify from the
 * strongest signals available: a non-null [cause] (network/IO/timeout exception)
 * and, failing that, the status code embedded in the message. Honoring `Retry-After`
 * would require carrying it on DomainResult.Error from the api layer (out of scope here).
 */
private val RETRYABLE_STATUS_CODES = listOf(408, 429, 500, 502, 503, 504)

private fun isTransientError(result: DomainResult.Error): Boolean {
    // A thrown exception (no HTTP status produced) is almost always transient:
    // connection reset, DNS, socket/read timeout, etc.
    if (result.cause != null) return true

    val msg = result.message?.lowercase() ?: return true

    // Textual hints that don't depend on a status code.
    if ("network" in msg || "timeout" in msg) return true

    // Match a retryable status only when it appears as a standalone number
    // (e.g. "Server error: 503"), so substrings like "500ms" or an id like
    // "5031" don't accidentally mark an error retryable.
    val numbers = Regex("\\d+").findAll(msg).map { it.value.toIntOrNull() }
    return numbers.any { it != null && it in RETRYABLE_STATUS_CODES }
}
